import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { logAdminAccess } from "@/lib/admin-audit.functions";
import { useState, useEffect } from "react";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  defaultBusiness,
  type BusinessConfig,
  reviews as defaultReviews,
  type ReviewConfig,
  areas as defaultAreas,
  type AreaConfig,
} from "@/lib/site-config";
import { Plus, Trash2 } from "lucide-react";
import { AdminAuthGate } from "@/components/admin/AdminAuthGate";
import { AdminUsersManager } from "@/components/admin/AdminUsersManager";
import type { AdminProfile } from "@/lib/admin-auth.functions";
import { AnalyticsDashboard } from "@/components/AnalyticsDashboard";
import { LeadsManager } from "@/components/admin/LeadsManager";
import { AreaConversionReport } from "@/components/admin/AreaConversionReport";
import { AreaPerformanceExport } from "@/components/admin/AreaPerformanceExport";
import { TrustContentEditor } from "@/components/admin/TrustContentEditor";
import { RoutingTargetsEditor } from "@/components/admin/RoutingTargetsEditor";
import { ComplianceEditor } from "@/components/admin/ComplianceEditor";
import { TemplateTester } from "@/components/admin/TemplateTester";
import { StagingVerification } from "@/components/admin/StagingVerification";
import { SchemaValidator } from "@/components/admin/SchemaValidator";
import { GbpWeeklyChecklist } from "@/components/admin/GbpWeeklyChecklist";
import { IndexingUrls } from "@/components/admin/IndexingUrls";
import { SiteStabilityTools } from "@/components/admin/SiteStabilityTools";
import { SourcePageLeads } from "@/components/admin/SourcePageLeads";
import { GA4EventTester } from "@/components/admin/GA4EventTester";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { GscMonitor } from "@/components/admin/GscMonitor";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { IndexingStatusWidget } from "@/components/admin/IndexingStatusWidget";
import { SeoSchemaChecker } from "@/components/admin/SeoSchemaChecker";
import { getAnalyticsDashboard } from "@/lib/analytics.functions";
import { TestimonialsManagerV2 } from "@/components/admin/TestimonialsManagerV2";
import {
  AreasManagerV2,
  ServicesManagerV2,
  BusinessInfoManager,
  BlogManager,
  RedirectsManager,
  SitemapManager,
} from "@/components/admin/CmsManagers";
import { MediaLibraryManager } from "@/components/admin/MediaLibraryManager";
import { MediaLibraryManagerV2 } from "@/components/admin/MediaLibraryManagerV2";
import { CmsSectionsManager } from "@/components/admin/CmsSectionsManager";
import { LaunchChecklistManager } from "@/components/admin/LaunchChecklistManager";
import { HeroOverlayManager } from "@/components/admin/HeroOverlayManager";
import { GbpOwnershipPanel } from "@/components/admin/GbpOwnershipPanel";
import { BusinessEntityManager } from "@/components/admin/BusinessEntityManager";
import { AddressVerificationPanel } from "@/components/admin/AddressVerificationPanel";
import { CitationChecklistManager } from "@/components/admin/CitationChecklistManager";

import { EmailSettingsManager } from "@/components/admin/EmailSettingsManager";
import { EmailLogsManager } from "@/components/admin/EmailLogsManager";
import { LeadFlowHealth } from "@/components/admin/LeadFlowHealth";
import { TestEnquiryFlow } from "@/components/admin/TestEnquiryFlow";
import { CtaStrategyQA } from "@/components/admin/CtaStrategyQA";
import { DriverLinkTrackingDashboard } from "@/components/admin/DriverLinkTrackingDashboard";
import { AdminShell } from "@/components/admin/AdminShell";
import { ADMIN_GROUPS, DEFAULT_VIEW, LEGACY_VIEW_REDIRECTS, resolveViewId, type AdminViewId } from "@/components/admin/AdminNavConfig";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { AdminPlaceholder } from "@/components/admin/AdminPlaceholder";
import { BadgeDebugPanel } from "@/components/admin/BadgeDebugPanel";
import { SeoChecklistManager } from "@/components/admin/SeoChecklistManager";
import { SeoChangeLogPanel } from "@/components/admin/SeoChangeLogPanel";
import { HealthcheckManager } from "@/components/admin/HealthcheckManager";
import { CwvMonitor } from "@/components/admin/CwvMonitor";
import { CmsBlocksManager } from "@/components/admin/CmsBlocksManager";
import { CmsLiveEditor } from "@/components/admin/CmsLiveEditor";
import { PagesManagerV2 } from "@/components/admin/PagesManagerV2";
import { GlobalBlocksManagerV2 } from "@/components/admin/GlobalBlocksManagerV2";
import { UnifiedContentManager } from "@/components/admin/UnifiedContentManager";
import { SeoPerPageEditor } from "@/components/admin/SeoPerPageEditor";
import { GeoAnswerReadyPanel } from "@/components/admin/GeoAnswerReadyPanel";
import { NapSourceOfTruth } from "@/components/admin/NapSourceOfTruth";
import { RedirectsManagerV2 } from "@/components/admin/RedirectsManagerV2";
import { SchemaManagerV2 } from "@/components/admin/SchemaManagerV2";
import { ScheduledPublishPanel } from "@/components/admin/ScheduledPublishPanel";
import { OffersManager } from "@/components/admin/OffersManager";
import { LeadsManagerV2 } from "@/components/admin/LeadsManagerV2";
import { AnalyticsDeepDive } from "@/components/admin/AnalyticsDeepDive";
import { EmailDeliveryMonitor } from "@/components/admin/EmailDeliveryMonitor";
import { FormsBuilderManager } from "@/components/admin/FormsBuilderManager";
import { OutreachManager } from "@/components/admin/OutreachManager";
import { HealthCentre } from "@/components/admin/HealthCentre";
import { SecurityHardeningPanel } from "@/components/admin/SecurityHardeningPanel";
import { AdminAccessLogPanel } from "@/components/admin/AdminAccessLogPanel";
import { FrontendErrorsPanel } from "@/components/admin/FrontendErrorsPanel";
import { BackupsExportsManager } from "@/components/admin/BackupsExportsManager";
import { IntegrationsHealthManager } from "@/components/admin/IntegrationsHealthManager";
import { HelpCentre } from "@/components/admin/HelpCentre";
import { LighthouseMonitor } from "@/components/admin/LighthouseMonitor";
import { JsonLdPreview } from "@/components/admin/JsonLdPreview";
import { CannibalisationAudit } from "@/components/admin/CannibalisationAudit";

// Valid views include both the currently-visible sidebar IDs and any legacy
// IDs that map to a current panel via LEGACY_VIEW_REDIRECTS. This keeps old
// bookmarks (?view=cms.pages) resolving instead of falling through to DEFAULT.
const VALID_VIEWS = new Set<string>([
  ...ADMIN_GROUPS.flatMap((g) => g.items.map((i) => i.id)),
  ...Object.keys(LEGACY_VIEW_REDIRECTS),
]);

const searchSchema = z.object({
  view: z
    .string()
    .optional()
    .transform((v) => (v && VALID_VIEWS.has(v) ? (v as AdminViewId) : undefined)),
});

export const Route = createFileRoute("/admin")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Admin — Woodseer Autos" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  return (
    <AdminAuthGate>
      {(admin, signOut) => <AdminPanel admin={admin} signOut={signOut} />}
    </AdminAuthGate>
  );
}

function AdminPanel({ admin, signOut }: { admin: AdminProfile; signOut: () => Promise<void> }) {
  const navigate = useNavigate({ from: "/admin" });
  const search = useSearch({ from: "/admin" });
  const view: AdminViewId = resolveViewId((search.view as AdminViewId) ?? DEFAULT_VIEW);
  const setView = (v: AdminViewId) =>
    navigate({ to: "/admin", search: { view: v }, replace: false });

  const [adminPassword, setAdminPassword] = useState<string>("");
  useEffect(() => {
    import("@/lib/admin-auth.functions").then(({ getLegacyAdminToken }) =>
      getLegacyAdminToken().then((r) => setAdminPassword(r.token)).catch(() => {}),
    );
  }, []);

  const [config, setConfig] = useState<BusinessConfig>(defaultBusiness);
  const [saved, setSaved] = useState(false);
  const [motClasses, setMotClasses] = useState("1, 2, 3, 4, 5, 7");
  const [motElectric, setMotElectric] = useState(false);
  const [motSameDay, setMotSameDay] = useState(true);
  const [siteReviews, setSiteReviews] = useState<ReviewConfig[]>(defaultReviews);
  const [siteAreas, setSiteAreas] = useState<AreaConfig[]>(defaultAreas);
  const [ga4Id, setGa4Id] = useState("");
  const [gadsId, setGadsId] = useState("");
  const [metaPixelId, setMetaPixelId] = useState("");

  useEffect(() => {
    try {
      const stored = localStorage.getItem("woodseer_business");
      if (stored) setConfig({ ...defaultBusiness, ...JSON.parse(stored) });
      const storedMot = localStorage.getItem("woodseer_mot_classes");
      if (storedMot) setMotClasses(storedMot);
      const storedReviews = localStorage.getItem("woodseer_reviews");
      if (storedReviews) setSiteReviews(JSON.parse(storedReviews));
      const storedAreas = localStorage.getItem("woodseer_areas");
      if (storedAreas) setSiteAreas(JSON.parse(storedAreas));
      const storedAnalytics = localStorage.getItem("woodseer_analytics");
      if (storedAnalytics) {
        const p = JSON.parse(storedAnalytics);
        setGa4Id(p.ga4Id || "");
        setGadsId(p.gadsId || "");
        setMetaPixelId(p.metaPixelId || "");
      }
    } catch {}
  }, []);

  function save() {
    localStorage.setItem("woodseer_business", JSON.stringify(config));
    localStorage.setItem("woodseer_mot_classes", motClasses);
    localStorage.setItem("woodseer_reviews", JSON.stringify(siteReviews));
    localStorage.setItem("woodseer_areas", JSON.stringify(siteAreas));
    localStorage.setItem("woodseer_analytics", JSON.stringify({ ga4Id, gadsId, metaPixelId }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  // Legacy deep-link bridge: convert old "admin:goto-tab" events to view IDs.
  useEffect(() => {
    function onGoto(e: Event) {
      const detail = (e as CustomEvent<unknown>).detail;
      let legacyTab: string | null = null;
      if (typeof detail === "string") legacyTab = detail;
      else if (detail && typeof detail === "object") {
        const d = detail as { tab?: string };
        legacyTab = d.tab ?? null;
      }
      const mapped = legacyTab ? LEGACY_TAB_TO_VIEW[legacyTab] : undefined;
      if (mapped) setView(mapped);
    }
    window.addEventListener("admin:goto-tab", onGoto as EventListener);
    return () => window.removeEventListener("admin:goto-tab", onGoto as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Audit: log every admin panel view (fire-and-forget; ignore failures).
  const logAccess = useServerFn(logAdminAccess);
  useEffect(() => {
    logAccess({ data: { action: "admin.view", target: view } }).catch(() => {});
  }, [view, logAccess]);


  return (
    <AdminShell
      view={view}
      onChange={setView}
      onSave={save}
      saved={saved}
      onSignOut={signOut}
      adminEmail={admin.email}
      adminRole={admin.role}
      isPrimaryOwner={admin.is_primary_owner}

    >
      <ViewSwitch
        view={view}
        admin={admin}
        adminPassword={adminPassword}
        config={config}
        setConfig={setConfig}
        motClasses={motClasses}
        setMotClasses={setMotClasses}
        motElectric={motElectric}
        setMotElectric={setMotElectric}
        motSameDay={motSameDay}
        setMotSameDay={setMotSameDay}
        siteReviews={siteReviews}
        setSiteReviews={setSiteReviews}
        siteAreas={siteAreas}
        setSiteAreas={setSiteAreas}
        ga4Id={ga4Id}
        setGa4Id={setGa4Id}
        gadsId={gadsId}
        setGadsId={setGadsId}
        metaPixelId={metaPixelId}
        setMetaPixelId={setMetaPixelId}
        onNavigate={setView}
      />
    </AdminShell>
  );
}

const LEGACY_TAB_TO_VIEW: Record<string, AdminViewId> = {
  Business: "settings.business",
  "Contact & Hours": "settings.contact",
  MOT: "settings.mot",
  Compliance: "settings.compliance",
  Photos: "cms.media",
  "Trust Editor": "settings.trust",
  Routing: "settings.routing",
  "Template Tester": "email.templates",
  Reviews: "settings.reviews",
  Areas: "settings.area-list",
  Leads: "leads.inbox",
  Analytics: "analytics.overview",
  "Area Reports": "analytics.areas",
  "Area Export": "analytics.export",
  Staging: "settings.staging",
  Schema: "seo.schema",
  Stability: "settings.stability",
  "GBP Weekly": "seo.gbp",
  Indexing: "seo.indexing",
  "GSC Monitor": "seo.gsc",
  "Indexing Status": "seo.indexing",
  "Schema Checker": "seo.schema-checker",
  "Reviews Manager": "cms.testimonials",
  "Source Pages": "leads.sources",
  "GA4 Test": "analytics.ga4",
  "Admin Users": "settings.users",
  "CMS: Testimonials": "cms.testimonials",
  "CMS: Areas": "cms.areas",
  "CMS: Services": "cms.services",
  "CMS: Business Info": "cms.business-info",
  "CMS: Blog": "cms.blog",
  "CMS: Media": "cms.media",
  "CMS: Redirects": "redirects",
  "CMS: Sitemap": "seo.sitemap",
  "CMS: Page Sections": "cms.pages",
  "Launch Checklist V2": "settings.launch",
  "Hero Overlay": "cms.hero",
  "GBP & Authority": "settings.gbp-authority",
  Citations: "seo.citations",
  "Site Health": "health.overview",
  "Lead Flow Health": "leads.flow-health",
  "Test Enquiry Flow": "leads.test",
  "CTA Strategy QA": "leads.cta-qa",
  "Driver Link Tracking": "leads.driver-links",
  "Email & Routing": "email.settings",
  "Email Logs": "email.logs",
};

interface ViewSwitchProps {
  view: AdminViewId;
  admin: AdminProfile;
  adminPassword: string;
  config: BusinessConfig;
  setConfig: (c: BusinessConfig) => void;
  motClasses: string;
  setMotClasses: (s: string) => void;
  motElectric: boolean;
  setMotElectric: (b: boolean) => void;
  motSameDay: boolean;
  setMotSameDay: (b: boolean) => void;
  siteReviews: ReviewConfig[];
  setSiteReviews: (r: ReviewConfig[]) => void;
  siteAreas: AreaConfig[];
  setSiteAreas: (a: AreaConfig[]) => void;
  ga4Id: string;
  setGa4Id: (v: string) => void;
  gadsId: string;
  setGadsId: (v: string) => void;
  metaPixelId: string;
  setMetaPixelId: (v: string) => void;
  onNavigate: (v: AdminViewId) => void;
}

function ViewSwitch(p: ViewSwitchProps) {
  const { view, adminPassword } = p;
  switch (view) {
    case "overview":
      return <AdminOverview onNavigate={p.onNavigate} />;

    /* ── CMS ── */
    case "cms.pages-v2":
      return <PagesManagerV2 />;
    case "cms.pages":
      return <CmsSectionsManager />;
    case "cms.blog":
      return <BlogManager />;
    case "cms.content":
      return <UnifiedContentManager />;
    case "cms.scheduled":
      return <ScheduledPublishPanel />;
    case "cms.offers":
      return <OffersManager />;
    case "cms.testimonials":
      return <TestimonialsManagerV2 />;
    case "cms.services":
      return <ServicesManagerV2 />;
    case "cms.areas":
      return <AreasManagerV2 />;
    case "cms.media":
      return <MediaLibraryManager />;
    case "cms.media-v2":
      return <MediaLibraryManagerV2 />;
    case "cms.gallery":
      return <GalleryManager password={adminPassword} />;
    case "cms.business-info":
      return <BusinessInfoManager />;
    case "cms.hero":
      return <HeroOverlayManager />;
    case "cms.blocks":
      return <CmsBlocksManager />;
    case "cms.blocks-v2":
      return <GlobalBlocksManagerV2 />;
    case "cms.editor":
      return <CmsLiveEditor />;
    case "cms.preview":
      return <LivePreviewPane />;

    /* ── SEO ── */
    case "seo.checklist":
      return <SeoChecklistManager />;
    case "seo.per-page":
      return <SeoPerPageEditor />;
    case "seo.geo":
      return <GeoAnswerReadyPanel />;
    case "seo.nap":
      return <NapSourceOfTruth />;
    case "seo.changelog":
      return <SeoChangeLogPanel />;
    case "seo.schema":
      return <SchemaValidator />;
    case "seo.schema-manager":
      return <SchemaManagerV2 />;
    case "seo.schema-checker":
      return <SeoSchemaChecker />;
    case "seo.jsonld-preview":
      return <JsonLdPreview />;
    case "seo.cannibalisation":
      return <CannibalisationAudit />;
    case "seo.sitemap":
      return <SitemapManager />;
    case "seo.indexing":
      return (
        <div className="space-y-6">
          <IndexingUrls />
          <IndexingStatusWidget password={adminPassword} />
        </div>
      );
    case "seo.gsc":
      return <GscMonitor password={adminPassword} />;
    case "seo.gbp":
      return <GbpWeeklyChecklist />;
    case "seo.citations":
      return <CitationChecklistManager />;

    /* ── Healthcheck ── */
    case "health.overview":
      return <HealthcheckManager filter="overview" />;
    case "health.links":
      return <HealthcheckManager filter="broken_link" />;
    case "health.schema":
      return <HealthcheckManager filter="schema" />;
    case "health.a11y":
      return <HealthcheckManager filter="a11y" />;
    case "health.performance":
      return <HealthcheckManager filter="performance" />;
    case "health.history":
      return <HealthcheckManager filter="history" />;

    /* ── Vitals ── */
    case "vitals":
      return <CwvMonitor />;

    /* ── Redirects ── */
    case "redirects":
      return <RedirectsManager />;
    case "redirects.v2":
      return <RedirectsManagerV2 />;

    /* ── Leads ── */
    case "leads.inbox":
      return <LeadsManager password={adminPassword} />;
    case "leads.crm":
      return <LeadsManagerV2 password={adminPassword} />;
    case "leads.flow-health":
      return <LeadFlowHealth password={adminPassword} />;
    case "leads.test":
      return <TestEnquiryFlow password={adminPassword} />;
    case "leads.sources":
      return <SourcePageLeads password={adminPassword} />;
    case "leads.cta-qa":
      return <CtaStrategyQA password={adminPassword} />;
    case "leads.driver-links":
      return <DriverLinkTrackingDashboard password={adminPassword} />;

    /* ── Analytics ── */
    case "analytics.overview":
      return (
        <AnalyticsTab
          password={adminPassword}
          ga4Id={p.ga4Id}
          setGa4Id={p.setGa4Id}
          gadsId={p.gadsId}
          setGadsId={p.setGadsId}
          metaPixelId={p.metaPixelId}
          setMetaPixelId={p.setMetaPixelId}
        />
      );
    case "analytics.deep-dive":
      return <AnalyticsDeepDive password={adminPassword} />;
    case "analytics.areas":
      return <AreaConversionReport password={adminPassword} />;
    case "analytics.export":
      return <AreaPerformanceExport password={adminPassword} />;
    case "analytics.ga4":
      return <GA4EventTester />;

    /* ── Email ── */
    case "email.settings":
      return <EmailSettingsManager password={adminPassword} />;
    case "email.delivery":
      return <EmailDeliveryMonitor password={adminPassword} />;
    case "email.logs":
      return <EmailLogsManager password={adminPassword} />;
    case "email.templates":
      return <TemplateTester password={adminPassword} />;

    /* ── Forms ── */
    case "cms.forms":
      return <FormsBuilderManager password={adminPassword} />;

    /* ── Health centre (unified) ── */
    case "health.centre":
      return <HealthCentre />;

    /* ── Outreach ── */
    case "outreach.prospects":
      return <OutreachManager />;

    /* ── Security hardening (admin/super_admin only) ── */
    case "settings.security":
      return <SecurityHardeningPanel role={p.admin.role} />;

    /* ── Admin access log (admin/super_admin only) ── */
    case "settings.access-log":
      return <AdminAccessLogPanel role={p.admin.role} />;

    /* ── Frontend runtime errors ── */
    case "ops.frontend-errors":
      return <FrontendErrorsPanel role={p.admin.role} />;


    /* ── Address & hours verification ── */
    case "settings.verification":
      return <AddressVerificationPanel />;




    /* ── Backups & ops (Turn 11) ── */
    case "ops.backups":
      return <BackupsExportsManager />;
    case "ops.integrations":
      return <IntegrationsHealthManager />;
    case "ops.help":
      return <HelpCentre />;
    case "ops.lighthouse":
      return <LighthouseMonitor />;
    case "ops.badge-debug":
      return <BadgeDebugPanel role={p.admin.role} />;


    /* ── Settings ── */
    case "settings.entity":
      return <BusinessEntityManager />;
    case "settings.business":
      return <BusinessIdentityPanel config={p.config} setConfig={p.setConfig} />;
    case "settings.contact":
      return <ContactHoursPanel config={p.config} setConfig={p.setConfig} />;
    case "settings.mot":
      return (
        <MotPanel
          motClasses={p.motClasses}
          setMotClasses={p.setMotClasses}
          motElectric={p.motElectric}
          setMotElectric={p.setMotElectric}
          motSameDay={p.motSameDay}
          setMotSameDay={p.setMotSameDay}
        />
      );
    case "settings.reviews":
      return <ReviewsLocalPanel reviews={p.siteReviews} setReviews={p.setSiteReviews} />;
    case "settings.area-list":
      return <AreasLocalPanel areas={p.siteAreas} setAreas={p.setSiteAreas} />;
    case "settings.compliance":
      return <ComplianceEditor password={adminPassword} />;
    case "settings.trust":
      return <TrustContentEditor password={adminPassword} />;
    case "settings.routing":
      return <RoutingTargetsEditor password={adminPassword} />;
    case "settings.gbp-authority":
      return <GbpOwnershipPanel />;
    case "settings.users":
      return <AdminUsersManager currentAdmin={p.admin} />;
    case "settings.launch":
      return <LaunchChecklistManager />;
    case "settings.stability":
      return <SiteStabilityTools />;
    case "settings.staging":
      return <StagingVerification />;

    default:
      return <AdminOverview onNavigate={p.onNavigate} />;
  }
}

/* ──────────────────────────────────────────────────────────────
   Inline panels (extracted from the previous tab body so the new
   sidebar can route to them as a regular React component).
   ────────────────────────────────────────────────────────────── */

function BusinessIdentityPanel({
  config,
  setConfig,
}: {
  config: BusinessConfig;
  setConfig: (c: BusinessConfig) => void;
}) {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Business identity</h1>
        <p className="mt-1 text-sm text-muted-foreground">Canonical NAP rendered across the site, footer, and JSON-LD.</p>
      </header>
      <Card title="Sitewide NAP verification">
        <div className="rounded-lg border border-border bg-background p-4 space-y-2 text-sm">
          <Row label="Name" value={config.name} />
          <Row label="Address" value={`${config.address}, ${config.addressLine2}, ${config.postcode}`} />
          <Row label="Phone" value={config.phone} />
          <Row label="Email" value={config.email} />
        </div>
      </Card>
      <Card title="Business identity">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Business Name" value={config.name} onChange={(v) => setConfig({ ...config, name: v })} />
          <Field label="VTS Number" value={config.vtsNumber} onChange={(v) => setConfig({ ...config, vtsNumber: v })} hint="DVSA MOT station number" />
        </div>
      </Card>
      <Card title="Address">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Street Address" value={config.address} onChange={(v) => setConfig({ ...config, address: v })} className="sm:col-span-2" />
          <Field label="City / Area" value={config.addressLine2} onChange={(v) => setConfig({ ...config, addressLine2: v })} />
          <Field label="Postcode" value={config.postcode} onChange={(v) => setConfig({ ...config, postcode: v })} />
          <Field label="Entrance Instructions" value={config.entrance} onChange={(v) => setConfig({ ...config, entrance: v })} hint="How customers find you" className="sm:col-span-2" />
        </div>
      </Card>
    </div>
  );
}

function ContactHoursPanel({
  config,
  setConfig,
}: {
  config: BusinessConfig;
  setConfig: (c: BusinessConfig) => void;
}) {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Contact & hours</h1>
      </header>
      <Card title="Contact details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone" value={config.phone} onChange={(v) => setConfig({ ...config, phone: v })} />
          <Field label="Phone Href" value={config.phoneHref} onChange={(v) => setConfig({ ...config, phoneHref: v })} hint="tel:+44…" />
          <Field label="Email" value={config.email} onChange={(v) => setConfig({ ...config, email: v })} />
          <Field label="WhatsApp URL" value={config.whatsappUrl} onChange={(v) => setConfig({ ...config, whatsappUrl: v })} />
          <Field label="Instagram Handle" value={config.instagram} onChange={(v) => setConfig({ ...config, instagram: v })} />
          <Field label="Instagram URL" value={config.instagramUrl} onChange={(v) => setConfig({ ...config, instagramUrl: v })} />
          <Field label="Google Maps URL" value={config.googleMapsUrl} onChange={(v) => setConfig({ ...config, googleMapsUrl: v })} className="sm:col-span-2" />
          <Field label="Google Maps Embed URL" value={config.googleMapsEmbedUrl} onChange={(v) => setConfig({ ...config, googleMapsEmbedUrl: v })} className="sm:col-span-2" hint="Share > Embed" />
        </div>
      </Card>
      <Card title="Opening hours">
        <div className="space-y-3">
          {config.hours.map((h, i) => (
            <div key={i} className="grid grid-cols-5 gap-3 items-end">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Day</Label>
                <Input value={h.day} onChange={(e) => { const hours = [...config.hours]; hours[i] = { ...hours[i], day: e.target.value }; setConfig({ ...config, hours }); }} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Time</Label>
                <Input value={h.time} onChange={(e) => { const hours = [...config.hours]; hours[i] = { ...hours[i], time: e.target.value }; setConfig({ ...config, hours }); }} />
              </div>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfig({ ...config, hours: config.hours.filter((_, idx) => idx !== i) })} aria-label={`Remove row ${i + 1}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setConfig({ ...config, hours: [...config.hours, { day: "", time: "" }] })} className="gap-1">
            <Plus className="h-3 w-3" /> Add row
          </Button>
        </div>
      </Card>
    </div>
  );
}

function MotPanel({
  motClasses,
  setMotClasses,
  motElectric,
  setMotElectric,
  motSameDay,
  setMotSameDay,
}: {
  motClasses: string;
  setMotClasses: (s: string) => void;
  motElectric: boolean;
  setMotElectric: (b: boolean) => void;
  motSameDay: boolean;
  setMotSameDay: (b: boolean) => void;
}) {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">MOT settings</h1>
      </header>
      <Card title="MOT capabilities">
        <Field label="MOT Classes (comma-separated)" value={motClasses} onChange={setMotClasses} hint="e.g. 1, 2, 3, 4, 5, 7" />
        <div className="flex flex-wrap items-center gap-6 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={motElectric} onCheckedChange={(v) => setMotElectric(!!v)} />
            <span className="text-sm text-foreground">Electric / hybrid testing available</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={motSameDay} onCheckedChange={(v) => setMotSameDay(!!v)} />
            <span className="text-sm text-foreground">Same-day repairs available</span>
          </label>
        </div>
      </Card>
    </div>
  );
}

function ReviewsLocalPanel({
  reviews,
  setReviews,
}: {
  reviews: ReviewConfig[];
  setReviews: (r: ReviewConfig[]) => void;
}) {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Reviews (local snippets)</h1>
        <p className="mt-1 text-sm text-muted-foreground">Browser-stored review excerpts rendered on the home and area pages.</p>
      </header>
      <div className="space-y-4">
        {reviews.map((r, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-foreground">Review {i + 1}</span>
              <Button variant="ghost" size="sm" className="text-destructive h-7" onClick={() => setReviews(reviews.filter((_, idx) => idx !== i))} aria-label="Remove review">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Name" value={r.name} onChange={(v) => { const rev = [...reviews]; rev[i] = { ...rev[i], name: v }; setReviews(rev); }} />
              <Field label="Service" value={r.service} onChange={(v) => { const rev = [...reviews]; rev[i] = { ...rev[i], service: v }; setReviews(rev); }} />
              <div className="space-y-1">
                <Label className="text-xs">Rating</Label>
                <Input type="number" min={1} max={5} value={r.rating} onChange={(e) => { const rev = [...reviews]; rev[i] = { ...rev[i], rating: Number(e.target.value) }; setReviews(rev); }} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Review text</Label>
              <Textarea rows={2} value={r.text} onChange={(e) => { const rev = [...reviews]; rev[i] = { ...rev[i], text: e.target.value }; setReviews(rev); }} />
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setReviews([...reviews, { name: "", text: "", rating: 5, service: "" }])} className="gap-1">
          <Plus className="h-3 w-3" /> Add review
        </Button>
      </div>
    </div>
  );
}

function AreasLocalPanel({
  areas,
  setAreas,
}: {
  areas: AreaConfig[];
  setAreas: (a: AreaConfig[]) => void;
}) {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Service areas (local list)</h1>
        <p className="mt-1 text-sm text-muted-foreground">Browser-stored area links. The CMS-backed list lives under CMS → Service areas.</p>
      </header>
      <div className="space-y-3">
        {areas.map((a, i) => (
          <div key={i} className="grid grid-cols-6 gap-3 items-end">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={a.name} onChange={(e) => { const ar = [...areas]; ar[i] = { ...ar[i], name: e.target.value }; setAreas(ar); }} />
            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-xs">Slug</Label>
              <Input value={a.slug} onChange={(e) => { const ar = [...areas]; ar[i] = { ...ar[i], slug: e.target.value }; setAreas(ar); }} />
            </div>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setAreas(areas.filter((_, idx) => idx !== i))} aria-label="Remove area">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setAreas([...areas, { slug: "", name: "", description: "" }])} className="gap-1">
          <Plus className="h-3 w-3" /> Add area
        </Button>
      </div>
    </div>
  );
}

function LivePreviewPane() {
  const routes = [
    { label: "Home", path: "/" },
    { label: "MOT", path: "/mot" },
    { label: "Servicing", path: "/servicing" },
    { label: "Diagnostics", path: "/diagnostics" },
    { label: "Bodywork", path: "/bodywork" },
    { label: "Mechanical", path: "/mechanical" },
    { label: "Booking", path: "/book" },
    { label: "Contact", path: "/contact" },
    { label: "Blog", path: "/blog" },
  ];
  const [path, setPath] = useState(routes[0].path);
  const [device, setDevice] = useState<"mobile" | "desktop">("mobile");
  const width = device === "mobile" ? 390 : 1280;
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Live preview</h1>
        <p className="mt-1 text-sm text-muted-foreground">Preview any public route as customers see it — mobile-first by default.</p>
      </header>
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={path}
          onChange={(e) => setPath(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-xs font-medium"
        >
          {routes.map((r) => (
            <option key={r.path} value={r.path}>{r.label}</option>
          ))}
        </select>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button onClick={() => setDevice("mobile")} className={`px-3 h-9 text-xs font-bold ${device === "mobile" ? "bg-primary text-primary-foreground" : "bg-background"}`}>Mobile</button>
          <button onClick={() => setDevice("desktop")} className={`px-3 h-9 text-xs font-bold ${device === "desktop" ? "bg-primary text-primary-foreground" : "bg-background"}`}>Desktop</button>
        </div>
        <a href={path} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary hover:underline">Open in new tab ↗</a>
      </div>
      <div className="rounded-xl border border-border bg-secondary/40 p-3 overflow-auto">
        <iframe
          key={path + device}
          src={path}
          title={`Preview of ${path}`}
          style={{ width, maxWidth: "100%" }}
          className="h-[720px] mx-auto bg-background rounded-md shadow-sm border border-border"
        />
      </div>
    </div>
  );
}

/* ─── Reusable UI ─── */
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4">
      <h2 className="text-sm font-bold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-bold text-foreground">{label}:</span>{" "}
      <span className="text-muted-foreground">{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, hint, className }: { label: string; value: string; onChange: (v: string) => void; hint?: string; className?: string }) {
  return (
    <div className={`space-y-1 ${className || ""}`}>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={hint} />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/* ─── Analytics tab (live dashboard + measurement IDs) ─── */
type DashData = Awaited<ReturnType<typeof getAnalyticsDashboard>>;
function AnalyticsTab({
  password, ga4Id, setGa4Id, gadsId, setGadsId, metaPixelId, setMetaPixelId,
}: {
  password: string;
  ga4Id: string; setGa4Id: (v: string) => void;
  gadsId: string; setGadsId: (v: string) => void;
  metaPixelId: string; setMetaPixelId: (v: string) => void;
}) {
  const [data, setData] = useState<DashData | null>(null);
  const [range, setRange] = useState<"24h" | "7d" | "30d" | "90d" | "all">("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getAnalyticsDashboard({ data: { password, range } })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load analytics"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [password, range]);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-foreground">Analytics dashboard</h1>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as typeof range)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-xs font-medium"
        >
          <option value="24h">Last 24h</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </header>

      {error && <div className="rounded-md bg-destructive/10 text-destructive text-xs px-3 py-2">{error}</div>}
      {loading && !data && <div className="text-sm text-muted-foreground">Loading…</div>}
      {data && <AnalyticsDashboard events={data.events} leads={data.leads} />}

      <Card title="Measurement IDs">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="GA4 measurement ID" value={ga4Id} onChange={setGa4Id} hint="G-XXXXXXX" />
          <Field label="Google Ads ID" value={gadsId} onChange={setGadsId} hint="AW-XXXXXXX" />
          <Field label="Meta Pixel ID" value={metaPixelId} onChange={setMetaPixelId} />
        </div>
      </Card>
    </div>
  );
}
